<script>
import {hasEditPermission, currentRoleLevelName} from '@/mixins/roleLevelPermissions';
import { mapState } from 'vuex';
import childNameOrChildren from '@/mixins/childNameOrChildren';

export default {
	mixins: [childNameOrChildren],
	props: ['agreementData'],
	data() {
		return {
			isViewMode : !hasEditPermission('section-parenting'),
			showLearnButton: ['client-level-1', 'client-level-2'].includes(currentRoleLevelName)
		};
		
	},
	computed: {
		...mapState(['currentRoleLevel']),
		parenting() {
			return this.agreementData.parenting;
			// return this.agreementData.parenting =  this.agreementData.parenting.hasOwnProperty('additionalClausesParentingMattersToRecitals') 
			// 	? this.agreementData.parenting 
			// 	: {...this.agreementData.parenting, additionalClausesParentingMattersToRecitals: []};;
		},
		parentingMatterTravelAreaOptions() {
			const options = ['Canada'];
			if (this.agreementData.agreement.updatedProvinceOfAgreement) {
				options.unshift(this.agreementData.agreement.updatedProvinceOfAgreement);
			}
			return options;
		},
		isUserSeparatingParty(){
			return this.currentRoleLevel.roleAssociated === 'party'
		},
		dynamicQuestions(){
			if(this.isUserSeparatingParty){
				return {
					travelInformArea: "What area will serve as a boundary during parenting time, beyond which you agree you will inform the other parent about the travel?",
					travelConsultArea: `Will you consult each other regarding travel details when either parent travels with ${this.childNameOrChildren}?`,
					newPartnerIntroduce: `Prior to introducing ${this.childNameOrChildren} to new romantic partners, will you inform or consult with the other parent?`,
					newPartnerCohabitate: "Prior to cohabiting with new romantic partners, will you inform or consult with the other parent?",
					annualReview: "Will you review the Parenting Plan annually?"
				}
			}
			else{
				return {
					travelInformArea: "The parties will inform one another regarding details of travel if they intend to travel during their parenting time with the children outside of what area",
					travelConsultArea: "The parties will consult one another regarding details of travel if they intend to travel during their parenting time with the children outside of what area",
					newPartnerIntroduce: "Prior to introducing the children to new romantic partners, will the parties inform or consult one another?",
					newPartnerCohabitate: "Prior to cohabiting with new romantic partners, will the parties inform or consult one another?",
					annualReview:"Will the parties perform an annual review of the Parenting Plan?"
				}
			}
		}
	},
	mounted(){
		this.agreementData.parenting =  this.agreementData.parenting.hasOwnProperty('additionalClausesParentingMattersToRecitals') 
			? this.agreementData.parenting 
			: {...this.agreementData.parenting, additionalClausesParentingMattersToRecitals: []};
	}
};
</script>

<template>
	<b-card>
		<div slot="header" class="section-heading">
			<h4>Other Parenting Matters</h4>
			<a href="#" onclick="Intercom('showArticle', 11106619)" v-if="this.showLearnButton">Learn &nbsp;<b-icon-book/></a>
		</div>

		<yes-no-choice
			v-model="parenting.travelClause"
			class="mb-3"
			label="Would you like to include standard travel provisions?"
			reset-undefined
			:testid="'parenting-matter-include-standard-provision'"
			:disabled="isViewMode"
			toolTipText="It is recommended that you include the standard travel clauses
			in your Agreement to outline how each of you will communicate around travel in the future."
		/>

		<conditional-group class="my-3" :condition="parenting.travelClause">
			<radio-group
				v-model="parenting.partnerHoldingPassport"
				input-cols="2"
				partner
				:options="['Either']"
				select
				class="mb-3"
				:agreement-data="agreementData"
				label="What parent will hold the passport?"
				:testid="'parenting-matter-passport-holder'"
				:length="'long'"
				:disabled="isViewMode"
				toolTipText="You can specify a particular parent will hold the passport, or more generally,
				and most commonly, that either parent may hold the passport."
			/>

			<radio-group
				v-model="parenting.travelInformArea"
				input-cols="4"
				class="mb-3"
				:label="dynamicQuestions.travelInformArea"
				other
				select
				:options="parentingMatterTravelAreaOptions"
				:testid="'parenting-matter-travel-inform-area'"
				:length="'long'"
				:disabled="isViewMode"
				toolTipText="This simply means that if you leave the city, area, province, country, you will inform the other parent of your plans,
				so that everyone knows the general whereabouts of the child.  Given that it happens during parenting time, it will not impact the other parent,
				it simply outlines the obligation to let the other parent know. (The most common answer to this question is the city that you live in)."
			/>

			<radio-group
				v-if="!isUserSeparatingParty"
				v-model="parenting.travelConsultArea"
				input-cols="4"
				class="mb-3"
				:label="dynamicQuestions.travelConsultArea"
				other
				select
				:options="parentingMatterTravelAreaOptions"
				:testid="'parenting-matter-travel-consult-area'"
				:length="'long'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<radio-group
			v-model="parenting.newPartnerIntroduce"
			input-cols="4"
			class="mb-3"
			:label="dynamicQuestions.newPartnerIntroduce"
			select
			:options="[
				{ value: 'inform', text: 'Inform' },
				{ value: 'consult', text: 'Consult' },
				{ value: false, text: 'No specification' },
			]"
			:testid="'parenting-matter-new-partner-introduction-to-children'"
			:disabled="isViewMode"
			toolTipText='It is important to recognize that this is with the intention to support the child during new transitions.
			Neither option means that the parent needs to ask permission of the other parent to have a new partner or introduce the child to a new partner.
			<b><i>Inform</i></b> means that you will give the other parent a "heads up" about your decision so that they are aware and have adequate information to support the child and answer questions.
			<b><i>Consult</i></b> means that you will have a conversation with the other parent about the plans to introduce the child to a new romantic partner and discuss where, when, and how that introduction might be done.'
		/>

		<radio-group
			v-model="parenting.newPartnerCohabitate"
			input-cols="4"
			class="mb-3"
			:label="dynamicQuestions.newPartnerCohabitate"
			select
			:options="[
				{ value: 'inform', text: 'Inform' },
				{ value: 'consult', text: 'Consult' },
				{ value: false, text: 'No specification' },
			]"
			:testid="'parenting-matter-new-partner-cohabiting'"
			:disabled="isViewMode"
			toolTipText='It is important to recognize that this is with the intention to support the child during new transitions.
			Neither option means that the parent needs to ask permission of the other parent to have a new partner or move in with a new partner.
			<b><i>Inform</i></b> means that you will give the other parent a "heads up" about your decision so that they are aware and have adequate information to support the child and answer questions.
			<b><i>Consult</i></b> means that you will have a conversation with the other parent about the plans to move in with a new romantic partner and discuss where, when, and how that might happen.'
		/>

		<conditional-group
			class="my-3"
			:condition="parenting.nestingArrangement === false"
		>
			<yes-no-choice
				v-model="parenting.separateSets"
				class="mb-3"
				label="Will the parties maintain separate sets of Children's items in each home?"
				reset-undefined
				:testid="'parenting-matter-separate-sets'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<yes-no-choice
			v-model="parenting.annualReview"
			class="mb-3"
			:label="dynamicQuestions.annualReview"
			reset-undefined
			:testid="'parenting-matter-annual-review'"
			:disabled="isViewMode"
			toolTipText="You may be proactive in having a review of your parenting plan or at any point, either party can elect to have a review and to make a new decision if necessary."
		/>
		<additional-clauses
			:title="'Additional Clauses into Recitals'" :newLabel="'Add Clause to Recitals'" class="mt-2"
			v-model="parenting.additionalClausesParentingMattersToRecitals" :testid="'parenting-matter-add-clause-to-recitals-'"
			:disabled="isViewMode"
		/>
		<additional-clauses
			:title="'Additional Clauses into Sections'" :newLabel="'Add Clause to Section'" class="mt-2"
			v-model="parenting.additionalClausesParentingMatters" :testid="'parenting-matter-add-clause-'"
			:disabled="isViewMode"
		/>
	</b-card>
</template>
